// Procedural 8-bit buddy sprites. Zero art files — seed in, cuteness out.

export function hashString(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const PALETTES = [
  ['#f5c4b3', '#d85a30'], // coral
  ['#cecbf6', '#7f77dd'], // lavender
  ['#9fe1cb', '#1d9e75'], // mint
  ['#f4c0d1', '#d4537e'], // sakura
  ['#fac775', '#ba7517'], // butter
  ['#b5d4f4', '#378add'], // sky
];

// legacy 8x8 generator — still the palette source for eggs
export function genSprite(seed) {
  const rnd = mulberry32(seed);
  const palette = PALETTES[Math.floor(rnd() * PALETTES.length)];
  return { palette };
}

// ---- the wardrobe: 22 hand-set accessory layers ----
// render twin of the wire registry in server/src/api.js — keys ARE the wire format.
// each design: { rarity, rows, pal } — rows are pixel strings on the sprite's own
// grid ('.' = empty), pal maps char -> color, or [a, b] for a two-frame shimmer.
// hats sit on the head top (float: crown/halo styles hover 1px), shoes stamp the
// feet row (once per foot), necklaces ride the neck line.
export const ACC_TINT = { common: '#9aa0b5', uncommon: '#7fd0b0', rare: '#f5c542', legendary: '#b18aff' };

export const ACCESSORIES = {
  hat: {
    sprout_cap: { rarity: 'common', rows: ['..t..', '.gsg.', 'ggggg'], pal: { t: '#5ee08f', s: '#1d9e75', g: '#47a25f' } },
    leaf_hat: { rarity: 'common', rows: ['...gs', 'ggggg', 'gggg.'], pal: { g: '#6fbf5a', s: '#7a5a3a' } },
    paper_boat: { rarity: 'common', rows: ['..ww..', '.wwww.', 'ffffff'], pal: { w: '#eee9da', f: '#cfc7b0' } },
    mushroom_cap: { rarity: 'uncommon', rows: ['.rrrr.', 'rwrrwr', 'RRRRRR'], pal: { r: '#d85a30', w: '#f4f1ea', R: '#b0421f' } },
    knit_beanie: { rarity: 'uncommon', rows: ['..pp..', '.bbbb.', 'bbbbbb', 'dbdbdb'], pal: { p: '#f4f1ea', b: '#d4537e', d: '#a83a60' } },
    flower_crown: { rarity: 'rare', rows: ['.f.y.f.', 'ggggggg'], pal: { f: '#f4a0c0', y: '#f5c542', g: '#3f9a55' } },
    wizard_hat: { rarity: 'rare', rows: ['...p...', '..pP...', '..ppP..', '.p*ppP.', 'ppppppp'], pal: { p: '#7f77dd', P: '#5a4fb0', '*': '#f5c542' } },
    golden_crown: { rarity: 'legendary', float: true, rows: ['g.g.g', 'gjgjg', 'ggggg'], pal: { g: '#f5c542', j: ['#d85a30', '#7fd0ff'] } },
  },
  shoes: {
    // side-profile grids, LEFT foot with the toe pointing left (col 0); the right
    // foot paints MIRRORED so both toes point outward — soles sit on the feet line
    grass_slippers: { rarity: 'common', rows: ['.gg', 'gGG'], pal: { g: '#5ee08f', G: '#1d9e75' } },
    wooden_clogs: { rarity: 'common', rows: ['.ww', 'www', 'ddd'], pal: { w: '#c99a66', d: '#6e4f30' } },
    red_sneakers: { rarity: 'uncommon', rows: ['.rr', 'rrr', 'www'], pal: { r: '#e8683c', w: '#f4f1ea' } },
    rain_boots: { rarity: 'uncommon', rows: ['.yY', '.yY', 'yyY', 'YYY'], pal: { y: '#ffd23b', Y: '#c79a2a' } },
    rocket_boots: { rarity: 'rare', rows: ['.mM', 'mmM', 'fff'], pal: { m: '#c0c6d8', M: '#6f7488', f: ['#ff9d3b', '#ffd23b'] } },
    ice_skates: { rarity: 'rare', rows: ['.ww', 'www', 'bbb'], pal: { w: '#e8f4fb', b: ['#5ab8e8', '#bfe8ff'] } },
    cloud_walkers: { rarity: 'legendary', rows: ['.cc', 'ccc', 'cCc'], pal: { c: ['#ffffff', '#dfe9fb'], C: ['#b8cef0', '#9fc0ee'] } },
  },
  necklace: {
    vine_loop: { rarity: 'common', rows: ['vvlvvv'], pal: { v: '#1d9e75', l: '#9fe1cb' } },
    berry_string: { rarity: 'common', rows: ['rbrbrb'], pal: { r: '#d4537e', b: '#7f77dd' } },
    shell_pendant: { rarity: 'uncommon', rows: ['ssssss', '..pp..'], pal: { s: '#c9b48c', p: '#f4c0d1' } },
    bell_collar: { rarity: 'uncommon', rows: ['cccccc', '..bb..'], pal: { c: '#b3573b', b: '#f5c542' } },
    pixel_bowtie: { rarity: 'rare', rows: ['bb.bb', 'bbkbb'], pal: { b: '#d85a30', k: '#8f2c12' } },
    star_locket: { rarity: 'rare', rows: ['cccccc', '..gg..'], pal: { c: '#c0c6d8', g: ['#f5c542', '#fff3c4'] } },
    rainbow_prism: { rarity: 'legendary', rows: ['cccccc', '..pq..', '..qp..'], pal: { c: '#c0c6d8', p: ['#ff8ab5', '#8ad2ff'], q: ['#f5c542', '#b18aff'] } },
  },
};

// stamp one design's grid at sprite-grid position (gx,gy), canvas offset ox/oy;
// mirror flips the profile so a right shoe's toe points right
function paintAccGrid(ctx, d, gx, gy, ox, oy, cell, frame, mirror = false) {
  const w = d.rows[0].length;
  for (let y = 0; y < d.rows.length; y++)
    for (let x = 0; x < d.rows[y].length; x++) {
      const ch = d.rows[y][mirror ? w - 1 - x : x];
      if (ch === '.') continue;
      let c = d.pal[ch];
      if (Array.isArray(c)) c = c[frame % 2];
      ctx.fillStyle = c;
      ctx.fillRect(ox + (gx + x) * cell, oy + (gy + y) * cell, cell, cell);
    }
}

// the legendary twinkle: one warm pixel, two frames on, two off
function accSparkle(ctx, gx, gy, ox, oy, cell) {
  ctx.fillStyle = '#fff3c4';
  const u = Math.max(1, Math.floor(cell / 1.5));
  ctx.fillRect(ox + gx * cell, oy + gy * cell, u, u);
}

// composite the equipped layers over an already-painted buddy — anchors ride the
// sprite (sprite.acc), so every stage and cell size lands hats/shoes/necklaces true
function paintEquip(ctx, sprite, ox, oy, cell, frame) {
  const eq = sprite.equip;
  const A = sprite.acc;
  if (!eq || !A) return;
  for (const slot of ['shoes', 'necklace', 'hat']) {
    const d = ACCESSORIES[slot]?.[eq[slot]];
    if (!d) continue;
    const w = d.rows[0].length;
    if (slot === 'shoes') {
      // soles ON the feet line (clamped on-canvas); toes point outward — the left
      // shoe wears its profile as drawn, the right one mirrored
      const gy = Math.min(A.feetY, SPRITE_H - 1) - (d.rows.length - 1);
      paintAccGrid(ctx, d, A.footL - 1, gy, ox, oy, cell, frame);
      paintAccGrid(ctx, d, A.footR, gy, ox, oy, cell, frame, true);
      if (d.rarity === 'legendary' && frame % 4 < 2) accSparkle(ctx, A.footR + w, gy + d.rows.length - 1, ox, oy, cell);
    } else if (slot === 'necklace') {
      const gx = A.cx - (w >> 1);
      paintAccGrid(ctx, d, gx, A.neckY, ox, oy, cell, frame);
      if (d.rarity === 'legendary' && frame % 4 < 2) accSparkle(ctx, gx + w, A.neckY + 1, ox, oy, cell);
    } else {
      // hats rest their bottom row right on the head top; clamped so a tall
      // hat on a tall-eared buddy squishes down instead of leaving the canvas
      const gy = Math.max(0, A.hatTop - d.rows.length - (d.float ? 1 : 0));
      const gx = A.cx - Math.ceil(w / 2);
      paintAccGrid(ctx, d, gx, gy, ox, oy, cell, frame);
      if (d.rarity === 'legendary' && frame % 4 < 2) accSparkle(ctx, frame % 8 < 4 ? gx - 1 : gx + w, gy, ox, oy, cell);
    }
  }
}

// a design standalone (closet chips, reveal cards) — shoes render as the pair
export function drawAccessory(canvas, slot, design, { cell = 4, frame = 0 } = {}) {
  const d = ACCESSORIES[slot]?.[design];
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (!d) return;
  const w = d.rows[0].length;
  const pair = slot === 'shoes';
  const tw = pair ? w * 2 + 1 : w;
  const gx = Math.floor((canvas.width / cell - tw) / 2);
  const gy = Math.floor((canvas.height / cell - d.rows.length) / 2);
  paintAccGrid(ctx, d, gx, gy, 0, 0, cell, frame);
  if (pair) paintAccGrid(ctx, d, gx + w + 1, gy, 0, 0, cell, frame, true); // a facing pair
  if (d.rarity === 'legendary' && frame % 4 < 2) accSparkle(ctx, gx + tw, Math.max(0, gy - 1), 0, 0, cell);
}

// ---- sprite 3.0: 12x12 half-mirrored bodies on a 12x14 grid ----
// values: 0 empty · 1 body · 2 shade · 3 eye · 4 outline · 5 blush · 6 gold · 7 tuft/white · 8 pattern
export const SPRITE_W = 12;
export const SPRITE_H = 14;
export const SPECIES = ['blob', 'cat', 'bird', 'ghost', 'bunny', 'dino', 'robot'];

export const RARITY = {
  common: { label: 'common', color: '#9b9ba3', p: 0.699 },
  uncommon: { label: 'uncommon ✦', color: '#34c98e', p: 0.2 },
  rare: { label: 'rare ✦✦', color: '#378add', p: 0.08 },
  epic: { label: 'epic ✦✦✦', color: '#7f77dd', p: 0.02 },
  legendary: { label: 'LEGENDARY ★', color: '#f5c542', p: 0.001 },
};

// hsl → hex, dependency-free
function hsl(h, s, l) {
  s /= 100;
  l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const to = (x) => Math.round(255 * x).toString(16).padStart(2, '0');
  return `#${to(f(0))}${to(f(8))}${to(f(4))}`;
}

export function spriteFor(seed, { stage = 'adult', wins = 0, equip = null } = {}) {
  const rnd = mulberry32(seed >>> 0);

  // 1. palette — 30% beloved classics, else a fresh macaron pair off the hue wheel
  let palette =
    rnd() < 0.3
      ? PALETTES[Math.floor(rnd() * PALETTES.length)]
      : (() => {
          const h = Math.floor(rnd() * 360);
          return [hsl(h, 62, 81), hsl(h, 52, 50)];
        })();

  // 2. species
  const species = SPECIES[Math.floor(rnd() * SPECIES.length)];

  // 3. rarity — its own roll so looks and luck stay independent
  const roll = rnd();
  const rarity =
    roll < RARITY.legendary.p
      ? 'legendary'
      : roll < RARITY.legendary.p + RARITY.epic.p
        ? 'epic'
        : roll < RARITY.legendary.p + RARITY.epic.p + RARITY.rare.p
          ? 'rare'
          : roll < 1 - RARITY.common.p
            ? 'uncommon'
            : 'common';
  if (rarity === 'rare') palette = rnd() < 0.5 ? ['#f5d97a', '#b8860b'] : ['#8d7be8', '#2a1b6e']; // gold or galaxy
  const shimmer =
    rarity === 'legendary'
      ? Array.from({ length: 6 }, (_, i) => {
          const h = Math.floor(rnd() * 60) + i * 60;
          return [hsl(h % 360, 62, 80), hsl(h % 360, 52, 50)];
        })
      : null;

  const g = Array.from({ length: SPRITE_H }, () => Array(SPRITE_W).fill(0));
  const bw = stage === 'juvenile' ? 8 : 10;
  const bh = stage === 'juvenile' ? 7 : stage === 'adult' ? 9 : 10;
  const left = (SPRITE_W - bw) / 2;
  const top = 12 - bh;
  const half = bw / 2;

  const set = (x, y, v) => {
    if (x >= 0 && x < SPRITE_W && y >= 0 && y < SPRITE_H) g[y][x] = v;
  };
  const mirror = (hx, y, v) => {
    set(left + hx, y, v);
    set(left + bw - 1 - hx, y, v);
  };

  // 4. species-shaped body mass (left half, mirrored)
  for (let y = 0; y < bh; y++)
    for (let hx = 0; hx < half; hx++) {
      const cy = y / (bh - 1) - 0.5;
      const cx = hx / (half - 1);
      let p = 0.35 + cx * 0.55 - Math.abs(cy) * 0.3;
      if (species === 'blob') p = 0.3 + cx * 0.5 - Math.abs(cy) * 0.65 + (cy > 0 ? 0.25 : 0); // round-bottomed
      if (species === 'cat') p -= cy < -0.2 ? 0.15 : 0; // slimmer head, no pig cheeks
      if (species === 'bunny') p = 0.25 + cx * 0.6 - Math.abs(cy) * 0.5 + (cy > 0.1 ? 0.2 : 0); // small + round
      if (species === 'ghost' && y === bh - 1) p = hx % 2 ? 0.95 : 0.05; // wavy hem
      if (species === 'bird' && y > bh * 0.6) p += 0.2; // plump belly
      if (species === 'dino') p += cy > 0 ? 0.15 : -0.05; // sturdy base
      if (species === 'robot') p = y === 0 || y === bh - 1 || hx >= half - 3 ? 0.97 : 0.85; // boxy chassis
      if (rnd() < p) mirror(hx, top + y, 1);
    }
  for (let y = top + 1; y < top + bh - 1; y++) for (let hx = 2; hx < half; hx++) mirror(hx, y, 1);

  // 5. species parts
  if (species === 'cat') {
    // proper tall ears, inset from the edges
    mirror(2, top - 1, 1);
    mirror(2, top - 2, 1);
    mirror(3, top - 1, 1);
    if (rnd() < 0.5) mirror(2, top - 3, 1); // extra-perky
  } else if (species === 'bunny') {
    // the long ears are the whole point
    for (let e = 1; e <= 4; e++) mirror(3, top - e, 1);
    if (rnd() < 0.4) mirror(3, top - 5, 1);
  } else if (species === 'bird') {
    set(left + half - 1, top - 1, 1);
    set(left + half, top - 1, 1);
    if (rnd() < 0.7) set(left + half - (rnd() < 0.5 ? 0 : 1), top - 2, 1);
    // wing nubs
    set(left - 1, top + Math.floor(bh / 2), 1);
    set(left + bw, top + Math.floor(bh / 2), 1);
  } else if (species === 'dino') {
    // back ridge spikes
    for (let sx = 1; sx < half + 1; sx += 2) set(left + sx, top - 1, 1);
    if (rnd() < 0.6) set(left + bw, top + bh - 2, 1); // tail nub
  } else if (species === 'robot') {
    set(left + half - 1, top - 1, 1);
    set(left + half - 1, top - 2, 6); // antenna ball, gold
  } else if (species === 'blob' && rnd() < 0.5) {
    mirror(2, top - 1, 1);
  }
  if (species !== 'ghost') {
    mirror(2, top + bh, 1);
    if (bw >= 10) mirror(3, top + bh, 1);
  }
  // optional tail curl for the fuzzy species
  if (['cat', 'bunny', 'dino'].includes(species) && rnd() < 0.6) {
    set(left + bw, top + bh - 3, 1);
    if (rnd() < 0.5) set(left + bw + 1 < SPRITE_W ? left + bw + 1 : left + bw, top + bh - 4, 1);
  }

  // 6. pattern pass — stripes / spots / dither, mirrored, on body cells only
  const patRoll = rarity === 'uncommon' ? 0.99 : rnd();
  const pattern = patRoll < 0.55 ? 'none' : patRoll < 0.75 ? 'spots' : patRoll < 0.9 ? 'stripes' : 'dither';
  if (pattern !== 'none')
    for (let y = 0; y < bh; y++)
      for (let hx = 0; hx < half; hx++) {
        const x = left + hx;
        const gy = top + y;
        if (g[gy]?.[x] !== 1) continue;
        const hit =
          pattern === 'stripes'
            ? y % 2 === 0
            : pattern === 'dither'
              ? (y + hx) % 2 === 0 && y > bh / 2
              : rnd() < 0.2;
        if (hit) mirror(hx, gy, 8);
      }

  // 7. shading
  for (let y = 0; y < SPRITE_H; y++)
    for (let hx = 0; hx < half; hx++) {
      const x = left + hx;
      if (g[y][x] === 1 && rnd() < (species === 'robot' ? 0.1 : 0.18)) {
        g[y][x] = 2;
        g[y][left + bw - 1 - hx] = 2;
      }
    }

  // 8. face — big wide-set eyes (two cells tall past juvenile); blush right below
  const eyeY = top + Math.max(1, Math.floor(bh * 0.35));
  const eyeHx = 2; // wide apart on every species — big eyes, big cute
  const tallEyes = stage !== 'juvenile';
  mirror(eyeHx, eyeY, 3);
  if (tallEyes) mirror(eyeHx, eyeY + 1, 3);
  mirror(eyeHx, eyeY + (tallEyes ? 2 : 1), 5);
  if (species === 'bird') set(left + half, eyeY + (tallEyes ? 1 : 0), 6); // golden beak between the eyes
  if (stage === 'elder') mirror(eyeHx, eyeY - 1, 7);

  // 9. crown from wins
  const tall = ['cat', 'bunny'].includes(species) ? 4 : species === 'bird' || species === 'dino' || species === 'robot' ? 3 : 2;
  const crownY = top - tall;
  if (wins >= 25) {
    for (let i = -2; i <= 2; i++) set(left + half + i - (bw % 2 ? 0 : 1), crownY, 6);
    set(left + half - 2, crownY - 1, 6);
    set(left + half, crownY - 1, 6);
    set(left + half + (bw % 2 ? 2 : 1), crownY - 1, 6);
  } else if (wins >= 10) {
    for (let i = -1; i <= 1; i++) set(left + half + i, crownY, 6);
    set(left + half, crownY - 1, 6);
  } else if (wins >= 3) {
    set(left + half, crownY, 6);
  }

  // 10. outline
  for (let y = 0; y < SPRITE_H; y++)
    for (let x = 0; x < SPRITE_W; x++) {
      if (g[y][x]) continue;
      const near =
        (y > 0 && g[y - 1][x] && g[y - 1][x] !== 4) ||
        (y < SPRITE_H - 1 && g[y + 1][x] && g[y + 1][x] !== 4) ||
        (x > 0 && g[y][x - 1] && g[y][x - 1] !== 4) ||
        (x < SPRITE_W - 1 && g[y][x + 1] && g[y][x + 1] !== 4);
      if (near) g[y][x] = 4;
    }

  // 11. accessory anchors — where a hat rests (topmost body pixel, ears count),
  // the neck line, and the feet row; equip layers composite against these
  let hatTop = top;
  scan: for (let y = 0; y < SPRITE_H; y++)
    for (let x = 0; x < SPRITE_W; x++)
      if (g[y][x] === 1 || g[y][x] === 2 || g[y][x] === 8) {
        hatTop = y;
        break scan;
      }
  const acc = { cx: left + half, hatTop, neckY: top + bh - 3, feetY: top + bh, footL: left + 2, footR: left + bw - 4 };

  return { grid: g, palette, species, stage, rarity, shimmer, hetero: rarity === 'epic', equip, acc };
}

export function rarityOf(seed) {
  return spriteFor(seed).rarity;
}

// paint a generated sprite onto a context (shared by widgets and the garden)
export function paintSprite(ctx, sprite, ox, oy, cell, { blink = false, napping = false, frame = 0 } = {}) {
  const { grid, shimmer, hetero } = sprite;
  // legendary buddies breathe rainbows, two steps per hue
  const palette = shimmer ? shimmer[Math.floor(frame / 2) % shimmer.length] : sprite.palette;
  for (let y = 0; y < SPRITE_H; y++)
    for (let x = 0; x < SPRITE_W; x++) {
      const v = grid[y][x];
      if (!v) continue;
      if (v === 3) {
        const isLower = y > 0 && grid[y - 1][x] === 3; // bottom half of a tall eye
        if (blink || napping) {
          ctx.fillStyle = palette[0];
          ctx.fillRect(ox + x * cell, oy + y * cell, cell, cell);
          if (!isLower) {
            ctx.fillStyle = '#232326';
            ctx.fillRect(ox + x * cell, oy + y * cell + Math.floor(cell / 2), cell, Math.max(1, Math.floor(cell / 4)));
          }
        } else {
          ctx.fillStyle = '#232326';
          ctx.fillRect(ox + x * cell, oy + y * cell, cell, cell);
          if (!isLower) {
            // epic buddies keep one rose eye — heterochromia as a badge of honor
            ctx.fillStyle = hetero && x >= SPRITE_W / 2 ? '#ff9de2' : '#ffffff';
            const hl = Math.max(1, Math.floor(cell / 2.5));
            ctx.fillRect(
              ox + x * cell + Math.max(1, Math.floor(cell / 5)),
              oy + y * cell + Math.max(1, Math.floor(cell / 5)),
              hl,
              hl
            );
          }
        }
        continue;
      }
      if (v === 8) {
        // pattern cells: body tone with a translucent deep wash — reads as a mid-tone
        ctx.fillStyle = palette[0];
        ctx.fillRect(ox + x * cell, oy + y * cell, cell, cell);
        ctx.globalAlpha = 0.45;
        ctx.fillStyle = palette[1];
        ctx.fillRect(ox + x * cell, oy + y * cell, cell, cell);
        ctx.globalAlpha = 1;
        continue;
      }
      ctx.fillStyle =
        v === 1 ? palette[0]
        : v === 2 ? palette[1]
        : v === 4 ? '#26262b'
        : v === 5 ? '#f2a7bd'
        : v === 6 ? '#f5c542'
        : '#f4f1ea';
      ctx.fillRect(ox + x * cell, oy + y * cell, cell, cell);
    }
  // legendary star companion, twinkling alongside
  if (shimmer && frame % 2 === 0) {
    ctx.fillStyle = '#fff3c4';
    ctx.fillRect(ox + (SPRITE_W - 1) * cell, oy + 2 * cell, Math.max(1, Math.floor(cell / 1.5)), Math.max(1, Math.floor(cell / 1.5)));
  }
  // the outfit rides on top of everything — hat over crown, shoes over feet
  paintEquip(ctx, sprite, ox, oy, cell, frame);
}

export function drawSprite(canvas, seed, { cell = 8, bob = 0, stage = 'adult', wins = 0, blink = false, napping = false, frame = 0, equip = null } = {}) {
  const sprite = spriteFor(seed, { stage, wins, equip });
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  paintSprite(ctx, sprite, 0, bob * cell, cell, { blink, napping, frame });
}

// idle animation: steppy bob, periodic blink; happiness sets the tempo
export function animateSprite(canvas, seed, { cell = 8, ms = 900, stage = 'adult', wins = 0, happiness = 'content', equip = null } = {}) {
  let frame = 0;
  let alive = true;
  const pace = happiness === 'ecstatic' ? 0.6 : happiness === 'happy' ? 0.85 : 1;
  const tick = () => {
    if (!alive || !canvas.isConnected) return;
    const blink = frame % 7 === 6;
    const bob = frame % 2 === 1 ? (happiness === 'ecstatic' ? 0.5 : 0.25) : 0;
    drawSprite(canvas, seed, { cell, bob, stage, wins, blink, frame, equip });
    if (happiness === 'ecstatic' && frame % 6 < 2) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#f2a7bd';
      ctx.fillRect((SPRITE_W - 2) * cell, cell, Math.max(1, cell / 2), Math.max(1, cell / 2));
    }
    frame++;
    setTimeout(tick, ms * pace);
  };
  tick();
  return () => {
    alive = false;
  };
}

export function petSeedFor(username) {
  return hashString(`vibebuddy:pet:${username}`);
}

// a cute default name, deterministic per buddy
const SYLLABLES = ['mo', 'chi', 'pi', 'ko', 'bu', 'ni', 'ta', 'ru', 'mi', 'po', 'zu', 'ki'];
export function suggestPetName(seed) {
  const rnd = mulberry32(seed ^ 0x5eed);
  const n = 2 + (rnd() < 0.3 ? 1 : 0);
  let name = '';
  for (let i = 0; i < n; i++) name += SYLLABLES[Math.floor(rnd() * SYLLABLES.length)];
  return name;
}

// the egg: same palette as the buddy inside, crack stages 0..3
export function drawEgg(canvas, seed, { cell = 8, crack = 0 } = {}) {
  const { palette } = genSprite(seed);
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const P = (x, y, c) => {
    ctx.fillStyle = c;
    ctx.fillRect(x * cell, y * cell, cell, cell);
  };
  const shell = '#f2ede2';
  const shade = '#d9d2c2';
  const ROWS = [
    [3, 4],
    [2, 5],
    [1, 6],
    [1, 6],
    [1, 6],
    [1, 6],
    [2, 5],
    [3, 4],
  ];
  ROWS.forEach(([a, b], y) => {
    for (let x = a; x <= b; x++) P(x, y + 1, x === b || y >= 6 ? shade : shell);
  });
  // palette speckles
  P(3, 3, palette[0]);
  P(5, 5, palette[0]);
  P(2, 6, palette[1]);
  // cracks
  if (crack >= 1) {
    P(4, 3, '#8a8378');
    P(3, 4, '#8a8378');
  }
  if (crack >= 2) {
    P(5, 4, '#8a8378');
    P(4, 5, '#8a8378');
    P(2, 5, '#8a8378');
  }
  if (crack >= 3) {
    P(4, 2, '#8a8378');
    P(5, 6, '#8a8378');
    P(3, 6, '#8a8378');
    P(4, 6, '#8a8378');
  }
}

// Procedural garden plants for flat mini views (8x8 cells).
// kind: seed | grass | flower | sapling | tree (the merge ladder, 3:1 each rung)
// every kind rolls 3 seeded variants so no two mini-gardens read the same
export function drawPlant(canvas, seed, kind, { cell = 4 } = {}) {
  const rnd = mulberry32(seed >>> 0);
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const px = (x, y, c) => {
    ctx.fillStyle = c;
    ctx.fillRect(x * cell, y * cell, cell, cell);
  };
  const stem = '#1d9e75';
  const leaf = '#9fe1cb';
  const dark = '#177a5c';
  const bark = '#7a5a3a';
  const head = PALETTES[Math.floor(rnd() * PALETTES.length)];
  const v = Math.floor(rnd() * 3); // the variant die
  if (kind === 'sprout') kind = 'grass'; // pre-2.0 rows, kindly re-labelled
  // soil
  px(2, 7, '#4a3a2c');
  px(3, 7, '#5c4936');
  px(4, 7, '#4a3a2c');
  px(5, 7, '#5c4936');
  if (kind === 'seed') {
    // a hopeful mound; the tip leans with the variant, one gets a lucky pebble
    px(3, 6, bark);
    px(4, 6, bark);
    px(3, 5, '#8a6a48');
    px(4, 5, '#8a6a48');
    px(v === 1 ? 3 : 4, 4, stem);
    if (v === 2) px(1, 6, '#9a93a8');
  } else if (kind === 'grass') {
    if (v === 0) {
      // the fountain
      px(1, 6, leaf);
      px(2, 6, stem);
      px(2, 5, leaf);
      px(3, 6, dark);
      px(3, 5, stem);
      px(3, 4, leaf);
      px(4, 6, stem);
      px(4, 5, dark);
      px(4, 4, stem);
      px(4, 3, leaf);
      px(5, 6, dark);
      px(5, 5, leaf);
    } else if (v === 1) {
      // windswept, leaning into the weather
      px(2, 6, dark);
      px(3, 6, stem);
      px(4, 6, stem);
      px(5, 6, dark);
      px(2, 5, stem);
      px(3, 5, leaf);
      px(4, 5, stem);
      px(2, 4, leaf);
      px(3, 4, stem);
      px(4, 3, leaf);
      px(5, 4, stem);
    } else {
      // a round tuft with a ladybug peeking out
      px(2, 6, stem);
      px(3, 6, dark);
      px(4, 6, stem);
      px(2, 5, dark);
      px(3, 5, leaf);
      px(2, 4, stem);
      px(4, 4, leaf);
      px(5, 6, '#d85a30');
    }
  } else if (kind === 'flower') {
    if (v === 0) {
      // tulip: a proud two-tone cup
      px(3, 6, stem);
      px(3, 5, stem);
      px(3, 4, stem);
      px(3, 3, stem);
      px(2, 4, leaf);
      px(4, 5, leaf);
      px(2, 2, head[0]);
      px(3, 2, head[1]);
      px(4, 2, head[0]);
      px(2, 1, head[0]);
      px(4, 1, head[0]);
    } else if (v === 1) {
      // daisy: petal cross around a golden heart
      px(4, 6, stem);
      px(4, 5, stem);
      px(4, 4, stem);
      px(5, 5, leaf);
      px(3, 2, head[0]);
      px(5, 2, head[0]);
      px(4, 1, head[0]);
      px(4, 3, head[0]);
      px(4, 2, '#f5c542');
    } else {
      // bellflower: a nodding bell on a curved stalk
      px(3, 6, stem);
      px(3, 5, stem);
      px(3, 4, stem);
      px(4, 3, stem);
      px(2, 5, leaf);
      px(4, 4, head[0]);
      px(5, 4, head[0]);
      px(4, 5, head[1]);
      px(5, 5, head[0]);
    }
  } else if (kind === 'sapling') {
    // a young tree with a real trunk and a loose canopy
    const canopy = v === 2 ? head[0] : leaf;
    px(3, 6, bark);
    px(3, 5, bark);
    px(3, 4, bark);
    if (v === 1) px(2, 5, bark); // a low branch stub
    px(2, 3, canopy);
    px(3, 3, canopy);
    px(4, 3, canopy);
    px(5, 3, v === 0 ? canopy : dark);
    px(2, 2, canopy);
    px(3, 2, dark);
    px(4, 2, canopy);
    px(3, 1, canopy);
  } else {
    // THE tree — trunk with roots, a canopy that fills the frame
    const canopy = v === 2 ? head[0] : leaf;
    px(3, 6, bark);
    px(4, 6, bark);
    px(3, 5, bark);
    px(4, 5, '#5c4126');
    px(2, 6, '#5c4126');
    px(5, 6, bark);
    for (let y = 0; y <= 4; y++)
      for (let x = 0; x <= 7; x++) {
        const w = y === 0 ? 1.5 : y === 1 ? 2.5 : y === 4 ? 2.5 : 3.5; // bulge then taper
        if (Math.abs(x - 3.5) <= w && rnd() < 0.94) px(x, y, rnd() < 0.18 ? dark : canopy);
      }
    px(v === 1 ? 2 : 5, 1, mixMini(canopy)); // crown light
    if (rnd() < 0.6) px(v === 0 ? 2 : 5, 3, head[1]); // seeded blossom
  }
}

// a cheap lighten for the mini tree's crown — full mix() lives in garden.js
function mixMini(hex) {
  const p = parseInt(hex.slice(1), 16);
  let out = '#';
  for (const sh of [16, 8, 0]) out += Math.min(255, ((p >> sh) & 255) + 48).toString(16).padStart(2, '0');
  return out;
}
